import React from "react";
import "./DevDashboardSkeleton.css";

/**
 * Skeleton pulse animation component (reuses the same pattern from DatabaseSkeleton)
 */
const SkeletonPulse = ({ width, height, borderRadius = "4px", className = "" }) => (
    <div
        className={`skeleton-pulse ${className}`}
        style={{ width, height, borderRadius }}
    />
);

/**
 * Skeleton for the 8 KPI cards row
 */
export const DevKpiCardsSkeleton = () => (
    <div className="dev-kpi-grid">
        {[
            { labelW: "90px", valueW: "50px" },
            { labelW: "95px", valueW: "50px" },
            { labelW: "115px", valueW: "40px" },
            { labelW: "60px", valueW: "55px" },
            { labelW: "75px", valueW: "48px" },
            { labelW: "110px", valueW: "60px" },
            { labelW: "85px", valueW: "40px" },
            { labelW: "75px", valueW: "95px" },
        ].map((item, i) => (
            <div key={i} className="dev-kpi-card dev-skeleton-kpi-card">
                <div className="dev-kpi-icon-box dev-skeleton-icon-box">
                    <SkeletonPulse width="18px" height="18px" borderRadius="4px" />
                </div>
                <div className="dev-kpi-body">
                    <SkeletonPulse width={item.labelW} height="11px" borderRadius="3px" />
                    <SkeletonPulse width={item.valueW} height="20px" borderRadius="4px" className="mt-1" />
                </div>
            </div>
        ))}
    </div>
);

/**
 * Skeleton for a single bar-chart analytics panel
 */
export const BarChartSkeleton = ({ titleW = "130px", barHeights = [45, 75, 35, 85, 60, 50, 90, 40] }) => (
    <div className="dev-analytics-card dev-skeleton-panel">
        {/* Panel Header */}
        <div className="dev-skeleton-panel-header">
            <SkeletonPulse width={titleW} height="14px" borderRadius="3px" />
            <SkeletonPulse width="90px" height="26px" borderRadius="4px" />
        </div>
        {/* Chart Area */}
        <div className="dev-skeleton-chart-area">
            {/* Y-axis labels */}
            <div className="dev-skeleton-y-axis">
                {[1, 2, 3, 4, 5].map((_, i) => (
                    <SkeletonPulse key={i} width="24px" height="8px" borderRadius="2px" />
                ))}
            </div>
            {/* Bars */}
            <div className="dev-skeleton-bars">
                {barHeights.map((h, idx) => (
                    <div key={idx} className="dev-skeleton-bar-col" style={{ height: "100%" }}>
                        <div className="dev-skeleton-bar-fill" style={{ height: `${h}%` }}>
                            <SkeletonPulse width="100%" height="100%" borderRadius="3px 3px 0 0" />
                        </div>
                    </div>
                ))}
            </div>
        </div>
        {/* X-axis labels */}
        <div className="dev-skeleton-x-axis">
            {barHeights.map((_, idx) => (
                <SkeletonPulse key={idx} width="28px" height="7px" borderRadius="2px" />
            ))}
        </div>
        {/* Legend */}
        <div className="dev-skeleton-legend">
            <SkeletonPulse width="8px" height="8px" borderRadius="50%" />
            <SkeletonPulse width="50px" height="8px" borderRadius="2px" />
        </div>
    </div>
);

/**
 * Skeleton for the Workflow Usage horizontal bar chart panel
 */
export const WorkflowChartSkeleton = () => (
    <div className="dev-analytics-card dev-skeleton-panel">
        {/* Panel Header */}
        <div className="dev-skeleton-panel-header">
            <SkeletonPulse width="155px" height="14px" borderRadius="3px" />
            <SkeletonPulse width="90px" height="26px" borderRadius="4px" />
        </div>
        {/* Horizontal bar items */}
        <div className="dev-skeleton-hbar-list">
            {[{ w: "85%", labelW: "55px" }, { w: "65%", labelW: "75px" }, { w: "45%", labelW: "110px" }, { w: "30%", labelW: "90px" }, { w: "20%", labelW: "100px" }].map((item, idx) => (
                <div key={idx} className="dev-skeleton-hbar-item">
                    <div className="dev-skeleton-hbar-row">
                        <SkeletonPulse width={item.labelW} height="12px" borderRadius="3px" />
                        <SkeletonPulse width="35px" height="12px" borderRadius="3px" />
                    </div>
                    <div className="dev-skeleton-hbar-track">
                        <SkeletonPulse width={item.w} height="7px" borderRadius="4px" />
                    </div>
                </div>
            ))}
        </div>
        {/* Legend Spacer to strictly match other panels */}
        <div className="dev-skeleton-legend" style={{ opacity: 0 }}>
            <span style={{ display: "inline-block", height: "18px" }}>&nbsp;</span>
        </div>
    </div>
);

/**
 * Skeleton for the Run Success/Failure dual-bar chart panel
 */
export const RunStatusChartSkeleton = () => (
    <div className="dev-analytics-card dev-skeleton-panel">
        {/* Panel Header */}
        <div className="dev-skeleton-panel-header">
            <SkeletonPulse width="165px" height="14px" borderRadius="3px" />
            <SkeletonPulse width="90px" height="26px" borderRadius="4px" />
        </div>
        {/* Chart Area */}
        <div className="dev-skeleton-chart-area">
            <div className="dev-skeleton-y-axis">
                {[1, 2, 3, 4, 5].map((_, i) => (
                    <SkeletonPulse key={i} width="24px" height="8px" borderRadius="2px" />
                ))}
            </div>
            <div className="dev-skeleton-bars">
                {[60, 45, 80, 50, 75, 90, 55, 65].map((h, idx) => (
                    <div key={idx} className="dev-skeleton-bar-col" style={{ height: "100%" }}>
                        <div className="dev-skeleton-bar-fill" style={{ height: `${h}%` }}>
                            <SkeletonPulse width="100%" height="100%" borderRadius="3px 3px 0 0" />
                        </div>
                    </div>
                ))}
            </div>
        </div>
        {/* X-axis labels */}
        <div className="dev-skeleton-x-axis">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((_, idx) => (
                <SkeletonPulse key={idx} width="28px" height="7px" borderRadius="2px" />
            ))}
        </div>
        {/* Dual Legend */}
        <div className="dev-skeleton-legend">
            <SkeletonPulse width="8px" height="8px" borderRadius="50%" />
            <SkeletonPulse width="55px" height="8px" borderRadius="2px" />
            <SkeletonPulse width="8px" height="8px" borderRadius="50%" className="ms-2" />
            <SkeletonPulse width="35px" height="8px" borderRadius="2px" />
        </div>
    </div>
);

/**
 * Skeleton for the 4 analytics panels row
 */
export const DevAnalyticsPanelsSkeleton = () => (
    <div className="dev-analytics-grid">
        <BarChartSkeleton titleW="130px" barHeights={[45, 75, 35, 85, 60, 50, 90, 40]} />
        <BarChartSkeleton titleW="140px" barHeights={[35, 60, 45, 80, 55, 70, 90, 65]} />
        <WorkflowChartSkeleton />
        <RunStatusChartSkeleton />
    </div>
);

/**
 * Skeleton for the Developer Performance section (4 cards)
 */
export const DevPerformanceSkeleton = () => (
    <div className="dev-performance-container dev-skeleton-perf">
        <div className="dev-performance-header">
            <SkeletonPulse width="200px" height="16px" borderRadius="3px" />
        </div>
        <div className="dev-performance-grid">
            {[...Array(3)].map((_, idx) => (
                <div key={idx} className="dev-perf-card dev-skeleton-perf-card">
                    <div className="dev-perf-icon-box dev-skeleton-icon-box">
                        <SkeletonPulse width="22px" height="22px" borderRadius="6px" />
                    </div>
                    <div className="dev-perf-info">
                        <SkeletonPulse width="110px" height="10px" borderRadius="3px" />
                        <SkeletonPulse width="140px" height="14px" borderRadius="3px" className="mt-1" />
                        <SkeletonPulse width="90px" height="10px" borderRadius="3px" className="mt-1" />
                    </div>
                </div>
            ))}
        </div>
    </div>
);

/**
 * Skeleton for the Machine Portfolio table
 */
export const DevPortfolioTableSkeleton = ({ tableHeight }) => (
    <div
        className="dev-portfolio-card dev-skeleton-portfolio"
        style={
            tableHeight
                ? {
                      height: `${tableHeight}px`,
                      maxHeight: `${tableHeight}px`,
                      minHeight: `${tableHeight}px`,
                  }
                : undefined
        }
    >
        {/* Header */}
        <div className="dev-portfolio-header dev-skeleton-portfolio-header">
            <SkeletonPulse width="160px" height="16px" borderRadius="3px" />
        </div>

        {/* Table Head matching Image 2 */}
        <div className="dev-skeleton-table-wrap">
            <div className="dev-skeleton-table-head">
                {["Machine Name", "Industry", "Category", "Created On", "Version", "Ratings", "Total Workflow", "Results", "Usages", "Machine 360"].map((_, cidx) => (
                    <div key={cidx} className="dev-skeleton-th">
                        <SkeletonPulse width={cidx === 0 ? "90px" : cidx === 9 ? "70px" : "55px"} height="12px" borderRadius="3px" />
                    </div>
                ))}
            </div>

            {/* Table Rows */}
            {[...Array(6)].map((_, ridx) => (
                <div key={ridx} className="dev-skeleton-table-row">
                    {[...Array(10)].map((_, cidx) => (
                        <div key={cidx} className="dev-skeleton-td">
                            <SkeletonPulse
                                width={cidx === 0 ? "110px" : cidx === 9 ? "26px" : "50px"}
                                height={cidx === 9 ? "26px" : "13px"}
                                borderRadius={cidx === 9 ? "50%" : "3px"}
                            />
                        </div>
                    ))}
                </div>
            ))}
        </div>
    </div>
);

/**
 * Skeleton for the Developer Information card
 */
export const DevInfoSkeleton = () => (
    <div className="dev-sidebar-card">
        <div className="dev-sidebar-header">
            <SkeletonPulse width="150px" height="15px" borderRadius="3px" />
            <SkeletonPulse width="40px" height="14px" borderRadius="3px" />
        </div>
        <div className="dev-info-list">
            {[...Array(11)].map((_, idx) => (
                <div key={idx} className="dev-info-row">
                    <SkeletonPulse width={idx % 2 === 0 ? "90px" : "110px"} height="12px" borderRadius="3px" />
                    <SkeletonPulse width={idx % 3 === 0 ? "80px" : "60px"} height="12px" borderRadius="3px" />
                </div>
            ))}
        </div>
    </div>
);

/**
 * Skeleton for the Skills / Categories card
 */
export const DevSkillsSkeleton = () => (
    <div className="dev-sidebar-card">
        <div className="dev-sidebar-header">
            <SkeletonPulse width="120px" height="15px" borderRadius="3px" />
        </div>
        <div className="dev-skills-wrap">
            {[55, 90, 75, 65, 80, 70, 85, 60, 95, 50].map((w, idx) => (
                <SkeletonPulse key={idx} width={`${w}px`} height="26px" borderRadius="4px" />
            ))}
        </div>
    </div>
);

/**
 * Skeleton for the Support & Notes card
 */
export const DevSupportSkeleton = () => (
    <div className="dev-sidebar-card dev-support-notes-card">
        <div className="dev-sidebar-header">
            <SkeletonPulse width="120px" height="15px" borderRadius="3px" />
        </div>
        <div className="dev-support-list dev-skeleton-support">
            {[...Array(4)].map((_, idx) => (
                <div key={idx} className="dev-support-item">
                    <SkeletonPulse width="16px" height="16px" borderRadius="3px" />
                    <SkeletonPulse width={idx === 0 ? "180px" : idx === 3 ? "80px" : "150px"} height="12px" borderRadius="3px" />
                </div>
            ))}
        </div>
    </div>
);

/**
 * Skeleton for the Sidebar (Developer Info + Skills + Support)
 */
export const DevSidebarSkeleton = () => (
    <aside className="dev-sidebar dev-skeleton-sidebar">
        <DevInfoSkeleton />
        <DevSkillsSkeleton />
        <DevSupportSkeleton />
    </aside>
);

/**
 * Full Developer Dashboard page skeleton
 * Matches the exact layout: KPI row → charts grid → performance → portfolio table + sidebar
 */
const DevDashboardSkeleton = () => (
    <div className="dashboard-main market-place-page-main main-dev-dahsboard dev-dashboard-root dev-dashboard-skeleton">
        {/* 1. Eight KPI Cards */}
        <DevKpiCardsSkeleton />

        {/* 2. Main Content Grid */}
        <div className="dev-main-grid">
            {/* Left Column */}
            <div className="dev-left-column">
                {/* Four Analytics Panels */}
                <DevAnalyticsPanelsSkeleton />

                {/* Developer Performance */}
                <DevPerformanceSkeleton />

                {/* Machine Portfolio Table */}
                <DevPortfolioTableSkeleton />
            </div>

            {/* Right Sidebar */}
            <DevSidebarSkeleton />
        </div>
    </div>
);

export default DevDashboardSkeleton;
